import type { CharacterAnimationAlias } from './types';

export type CharacterAnimationBundle = 'locomotion' | 'actions' | 'boardActions' | 'emotes';
export type CharacterAnimationLoopPolicy = 'repeat' | 'once' | 'clamp';
export type CharacterAnimationPriority = 'idle' | 'emote' | 'movement' | 'critical';
export type CharacterAnimationScope = 'shared' | 'board' | 'minigame' | 'lobby';

export interface CharacterAnimationCapability {
  readonly alias: CharacterAnimationAlias;
  readonly bundle: CharacterAnimationBundle;
  readonly sourceClip: string;
  readonly loop: CharacterAnimationLoopPolicy;
  readonly priority: CharacterAnimationPriority;
  readonly defaultTimeScale?: number;
  readonly scopes: readonly CharacterAnimationScope[];
}

export const CHARACTER_ANIMATION_CAPABILITIES = [
  {
    alias: 'walk',
    bundle: 'locomotion',
    sourceClip: 'Armature|walking_man|baselayer',
    loop: 'repeat',
    priority: 'movement',
    scopes: ['shared'],
  },
  {
    alias: 'idleTurnLeft',
    bundle: 'locomotion',
    sourceClip: 'Armature|Idle_Turn_Left|baselayer',
    loop: 'repeat',
    priority: 'movement',
    scopes: ['shared'],
  },
  {
    alias: 'idleTurnRight',
    bundle: 'locomotion',
    sourceClip: 'Armature|Idle_Turn_Right|baselayer',
    loop: 'repeat',
    priority: 'movement',
    scopes: ['shared'],
  },
  {
    alias: 'walkTurnLeft',
    bundle: 'locomotion',
    sourceClip: 'Armature|Walk_Turn_Left|baselayer',
    loop: 'repeat',
    priority: 'movement',
    scopes: ['shared'],
  },
  {
    alias: 'walkTurnRight',
    bundle: 'locomotion',
    sourceClip: 'Armature|Walk_Turn_Right|baselayer',
    loop: 'repeat',
    priority: 'movement',
    scopes: ['shared'],
  },
  {
    alias: 'punch',
    bundle: 'actions',
    sourceClip: 'Armature|Punch_Combo|baselayer',
    loop: 'once',
    priority: 'critical',
    scopes: ['board', 'minigame'],
  },
  {
    alias: 'knockdown',
    bundle: 'actions',
    sourceClip: 'Armature|Shot_and_Blown_Back|baselayer',
    loop: 'once',
    priority: 'critical',
    scopes: ['board', 'minigame'],
  },
  {
    alias: 'standUp',
    bundle: 'actions',
    sourceClip: 'Armature|Stand_Up8|baselayer',
    loop: 'once',
    priority: 'critical',
    scopes: ['board', 'minigame'],
  },
  {
    alias: 'defeat',
    bundle: 'actions',
    sourceClip: 'Armature|Dead|baselayer',
    loop: 'clamp',
    priority: 'critical',
    scopes: ['shared'],
  },
  {
    alias: 'victory',
    bundle: 'actions',
    sourceClip: 'Armature|victory|baselayer',
    loop: 'clamp',
    priority: 'critical',
    scopes: ['shared'],
  },
  {
    alias: 'diceStretch',
    bundle: 'boardActions',
    sourceClip: 'Armature|Idle_03|baselayer',
    loop: 'once',
    priority: 'critical',
    scopes: ['board'],
  },
  {
    alias: 'diceCatch',
    bundle: 'boardActions',
    sourceClip: 'Armature|Jump_and_Grab_Wall|baselayer',
    loop: 'clamp',
    priority: 'critical',
    scopes: ['board'],
  },
  {
    alias: 'drink',
    bundle: 'boardActions',
    sourceClip: 'Armature|Stand_and_Drink|baselayer',
    loop: 'once',
    priority: 'critical',
    scopes: ['board'],
  },
  {
    alias: 'emoteGG',
    bundle: 'emotes',
    sourceClip: 'Armature|Indoor_Swing|baselayer',
    loop: 'once',
    priority: 'emote',
    scopes: ['shared', 'lobby'],
  },
  {
    alias: 'emoteOof',
    bundle: 'emotes',
    sourceClip: 'Armature|Shot_in_the_Back_and_Fall|baselayer',
    loop: 'once',
    priority: 'emote',
    scopes: ['shared', 'lobby'],
  },
  {
    alias: 'emoteTaunt',
    bundle: 'emotes',
    sourceClip: 'Armature|golf_drive|baselayer',
    loop: 'once',
    priority: 'emote',
    scopes: ['shared', 'lobby'],
  },
  {
    alias: 'emoteThanks',
    bundle: 'emotes',
    sourceClip: 'Armature|happy_jump_m|baselayer',
    loop: 'once',
    priority: 'emote',
    scopes: ['shared', 'lobby'],
  },
  {
    alias: 'emoteCry',
    bundle: 'emotes',
    sourceClip: 'Armature|Angry_To_Tantrum_Sit|baselayer',
    loop: 'once',
    priority: 'emote',
    scopes: ['shared', 'lobby'],
  },
  {
    alias: 'emoteCheer',
    bundle: 'emotes',
    sourceClip: 'Armature|Cheer_with_Both_Hands|baselayer',
    loop: 'once',
    priority: 'emote',
    scopes: ['shared', 'lobby'],
  },
] as const satisfies readonly CharacterAnimationCapability[];

export const CHARACTER_ANIMATION_CAPABILITIES_BY_ALIAS = new Map<
  string,
  CharacterAnimationCapability
>(CHARACTER_ANIMATION_CAPABILITIES.map((capability) => [capability.alias, capability]));

export const CHARACTER_ANIMATION_CAPABILITIES_BY_SOURCE_CLIP = new Map<
  string,
  CharacterAnimationCapability
>(
  CHARACTER_ANIMATION_CAPABILITIES.map((capability) => [
    capability.sourceClip.toLowerCase(),
    capability,
  ]),
);

export const CHARACTER_ANIMATION_ALIASES_BY_BUNDLE = CHARACTER_ANIMATION_CAPABILITIES.reduce(
  (bundles, capability) => {
    bundles[capability.bundle].push(capability.alias);
    return bundles;
  },
  {
    locomotion: [] as CharacterAnimationAlias[],
    actions: [] as CharacterAnimationAlias[],
    boardActions: [] as CharacterAnimationAlias[],
    emotes: [] as CharacterAnimationAlias[],
  },
);

export function getCharacterAnimationCapability(
  aliasOrClipName: CharacterAnimationAlias | string,
): CharacterAnimationCapability | undefined {
  return (
    CHARACTER_ANIMATION_CAPABILITIES_BY_ALIAS.get(aliasOrClipName as CharacterAnimationAlias) ??
    CHARACTER_ANIMATION_CAPABILITIES_BY_SOURCE_CLIP.get(aliasOrClipName.toLowerCase())
  );
}
